export type DateLike = Date | string

export interface SerializableError {
  name: string
  message: string
  stack?: string
  cause?: unknown
}

export const toSerializableDate = <T extends DateLike | null | undefined>(value: T): string | null | undefined => {
  if (value == null) {
    return value
  }
  return value instanceof Date ? value.toISOString() : value
}

export const toHydratedDate = <T extends DateLike | null | undefined>(value: T): Date | null | undefined => {
  if (value == null) {
    return value
  }
  return value instanceof Date ? value : new Date(value)
}

export const toSerializableSet = <T>(input: Set<T> | T[] | undefined): T[] | undefined => {
  if (!input) {
    return undefined
  }
  const list = Array.isArray(input) ? input : Array.from(input)
  return Array.from(new Set(list))
}

export const toHydratedSet = <T>(input: Set<T> | T[] | undefined): Set<T> | undefined => {
  if (!input) {
    return undefined
  }
  return input instanceof Set ? input : new Set(input)
}

export const toSerializableStringMap = <T>(
  input: Map<string, T> | Record<string, T> | undefined,
): Record<string, T> | undefined => {
  if (!input) {
    return undefined
  }
  if (input instanceof Map) {
    return Object.fromEntries(input)
  }
  return input
}

export const toHydratedStringMap = <T>(
  input: Map<string, T> | Record<string, T> | undefined,
): Map<string, T> | undefined => {
  if (!input) {
    return undefined
  }
  if (input instanceof Map) {
    return input
  }
  return new Map(Object.entries(input))
}

export const mergeUniqueItems = <T>(a?: T[], b?: T[]): T[] | undefined => {
  if (a && b) {
    return Array.from(new Set([...a, ...b]))
  }
  return a || b
}

export const toSerializableError = (error: unknown): unknown => {
  if (!error) {
    return error
  }

  if (error instanceof Error) {
    const serializableError: SerializableError = {
      name: error.name,
      message: error.message,
    }

    if (typeof error.stack === 'string') {
      serializableError.stack = error.stack
    }

    const errorWithCause = error as Error & {cause?: unknown}
    if (errorWithCause.cause !== undefined) {
      serializableError.cause = errorWithCause.cause
    }

    return serializableError
  }

  const unknownError = error as {name?: unknown; message?: unknown; stack?: unknown; cause?: unknown}
  if (typeof unknownError.name === 'string' && typeof unknownError.message === 'string') {
    const serializableError: SerializableError = {
      name: unknownError.name,
      message: unknownError.message,
    }

    if (typeof unknownError.stack === 'string') {
      serializableError.stack = unknownError.stack
    }

    if (unknownError.cause !== undefined) {
      serializableError.cause = unknownError.cause
    }

    return serializableError
  }

  return error
}

export const toSerializableValue = (value: unknown): unknown => {
  if (value == null) {
    return value
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (value instanceof Set) {
    return Array.from(value).map((item) => toSerializableValue(item))
  }

  if (value instanceof Map) {
    return Object.fromEntries(
      Array.from(value.entries()).map(([key, entryValue]) => {
        return [key, toSerializableValue(entryValue)]
      }),
    )
  }

  if (value instanceof Error) {
    return toSerializableError(value)
  }

  if (Array.isArray(value)) {
    return value.map((item) => toSerializableValue(item))
  }

  if (typeof value === 'object') {
    const recordValue = value as Record<string, unknown>
    return Object.entries(recordValue).reduce((result, [key, entryValue]) => {
      return {
        ...result,
        [key]: toSerializableValue(entryValue),
      }
    }, {})
  }

  return value
}

export const toSerializableEntityModelDates = <T extends {createdAt?: DateLike; updatedAt?: DateLike}>(
  entity: T,
): T => {
  return {
    ...entity,
    createdAt: toSerializableDate(entity.createdAt),
    updatedAt: toSerializableDate(entity.updatedAt),
  }
}

export const toHydratedEntityModelDates = <T extends {createdAt?: DateLike; updatedAt?: DateLike}>(entity: T): T => {
  return {
    ...entity,
    createdAt: toHydratedDate(entity.createdAt),
    updatedAt: toHydratedDate(entity.updatedAt),
  }
}
